const DB_NAME = "nurion-chat-drafts";
const STORE_NAME = "drafts";
const DB_VERSION = 1;

/** Upper bound on draft body length. Truncate-with-warning over this. */
export const MAX_DRAFT_LENGTH = 100_000;

/** Debounce window for `saveDraft` writes. Pinned by the ticket. */
export const DRAFT_SAVE_DEBOUNCE_MS = 300;

interface DraftRow {
  body: string;
  updatedAt: number;
}

/**
 * Build the storage key for a draft. Threaded drafts live alongside
 * the main composer's draft for the same room — separate keys mean
 * one composer's content never overwrites the other.
 */
export function draftKey(
  userId: string,
  roomId: string,
  threadRootId: string | null | undefined,
): string {
  return `${userId}::${roomId}::${threadRootId ?? "main"}`;
}

/** Result of `saveDraft`. `truncated` is true when the body was clipped. */
export interface SaveDraftResult {
  truncated: boolean;
  /** True when the (clipped) body became empty and we deleted instead. */
  deleted: boolean;
}

let legacyMigrated = false;

export function resetDraftsForTest(): void {
  legacyMigrated = false;
  for (const entry of debounceQueue.values()) clearTimeout(entry.timer);
  debounceQueue.clear();
  debounceListeners.clear();
  keysListeners.clear();
}

async function migrateLegacyDrafts(): Promise<void> {
  if (legacyMigrated) return;
  legacyMigrated = true;
  await deleteLegacyDrafts();
}

async function deleteLegacyDrafts(): Promise<void> {
  try {
    const keys = await withStore<IDBValidKey[]>(
      "readonly",
      (store) => store.getAllKeys() as IDBRequest<IDBValidKey[]>,
    );
    const legacy = (keys ?? []).filter(
      (key): key is string => typeof key === "string" && key.split("::").length === 2,
    );
    if (legacy.length === 0) return;
    await withStore("readwrite", (store) => {
      for (const key of legacy) store.delete(key);
    });
    notifyDraftKeysChanged();
  } catch {
    // Best-effort.
  }
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  let db: IDBDatabase;
  try {
    db = await openDb();
  } catch {
    // Persistence is best-effort. A browser without IDB (or with it
    // disabled) just falls back to in-memory state in the composer.
    return undefined;
  }
  return await new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    let value: T | undefined;
    const request = run(store);
    if (request) {
      request.onsuccess = () => {
        value = request.result;
      };
    }
    tx.oncomplete = () => {
      db.close();
      resolve(value);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("draft tx aborted"));
    };
  });
}

/**
 * Read a draft body for `key`. Missing rows return `""` so the
 * caller can hydrate the textarea unconditionally.
 */
export async function loadDraft(key: string): Promise<string> {
  try {
    await migrateLegacyDrafts();
    const value = await withStore<DraftRow | undefined>("readonly", (store) => store.get(key));
    if (!value) return "";
    return typeof value.body === "string" ? value.body : "";
  } catch {
    return "";
  }
}

export async function wipeDraftsForUser(userId: string): Promise<void> {
  if (!userId) return;
  const prefix = `${userId}::`;
  try {
    for (const key of debounceQueue.keys()) {
      if (key.startsWith(prefix)) cancelPendingDraftSave(key);
    }
    const keys = await withStore<IDBValidKey[]>(
      "readonly",
      (store) => store.getAllKeys() as IDBRequest<IDBValidKey[]>,
    );
    const matching = (keys ?? []).filter(
      (key): key is string =>
        typeof key === "string" && (key.startsWith(prefix) || key.split("::").length === 2),
    );
    if (matching.length > 0) {
      await withStore("readwrite", (store) => {
        for (const key of matching) store.delete(key);
      });
    }
    if (matching.length > 0) notifyDraftKeysChanged();
  } catch {
    // Best-effort.
  }
}

/**
 * Persist a draft body for `key`. Empty / whitespace-only bodies are
 * stored as a deletion. Returns whether the body was clipped to
 * `MAX_DRAFT_LENGTH` and whether the row was deleted.
 */
export async function saveDraft(key: string, body: string): Promise<SaveDraftResult> {
  const truncated = body.length > MAX_DRAFT_LENGTH;
  const next = truncated ? body.slice(0, MAX_DRAFT_LENGTH) : body;
  if (next.trim().length === 0) {
    await deleteDraft(key);
    return { truncated, deleted: true };
  }
  const row: DraftRow = { body: next, updatedAt: Date.now() };
  try {
    await withStore("readwrite", (store) => {
      store.put(row, key);
    });
  } catch {
    // Quota / disabled storage — the composer keeps its in-memory
    // value; the user's typing experience is unaffected.
  }
  return { truncated, deleted: false };
}

/** Delete a draft. Idempotent: deleting a missing row is a no-op. */
export async function deleteDraft(key: string): Promise<void> {
  try {
    await withStore("readwrite", (store) => {
      store.delete(key);
    });
  } catch {
    // Best-effort.
  }
}

/**
 * List every key with a stored draft. Used by the sidebar to decorate
 * room rows with the per-room draft indicator. The list reflects the
 * current store snapshot — callers re-list after `saveDraft` /
 * `deleteDraft` writes (or subscribe via `subscribeDraftKeys`).
 */
export async function listDraftKeys(): Promise<string[]> {
  try {
    const keys = await withStore<IDBValidKey[]>(
      "readonly",
      (store) => store.getAllKeys() as IDBRequest<IDBValidKey[]>,
    );
    if (!keys) return [];
    return keys.filter((k): k is string => typeof k === "string");
  } catch {
    return [];
  }
}

/* ─── debounced save ──────────────────────────────────────────── */

type DebounceEntry = {
  timer: ReturnType<typeof setTimeout>;
  pending: string;
};

const debounceQueue = new Map<string, DebounceEntry>();
type DebounceListener = (result: SaveDraftResult, key: string) => void;
const debounceListeners = new Set<DebounceListener>();

/**
 * Schedule a debounced save for `key`. Calls before the window
 * elapses overwrite the pending body — only the last value wins.
 * The returned promise resolves when the next flush completes, with
 * `null` when superseded by a later call.
 */
export function saveDraftDebounced(key: string, body: string): void {
  const existing = debounceQueue.get(key);
  if (existing) {
    clearTimeout(existing.timer);
  }
  const entry: DebounceEntry = {
    pending: body,
    timer: setTimeout(() => {
      const final = debounceQueue.get(key);
      debounceQueue.delete(key);
      if (!final) return;
      void saveDraft(key, final.pending).then((result) => {
        for (const listener of debounceListeners) listener(result, key);
        for (const listener of keysListeners) listener();
      });
    }, DRAFT_SAVE_DEBOUNCE_MS),
  };
  debounceQueue.set(key, entry);
}

/**
 * Flush any pending debounced save for `key` immediately. Returns the
 * resolved `SaveDraftResult` if a write happened, `null` if nothing
 * was pending. Used on submit / unmount so transient edits don't get
 * dropped if the composer tears down inside the debounce window.
 */
export async function flushDraft(key: string): Promise<SaveDraftResult | null> {
  const entry = debounceQueue.get(key);
  if (!entry) return null;
  clearTimeout(entry.timer);
  debounceQueue.delete(key);
  const result = await saveDraft(key, entry.pending);
  for (const listener of debounceListeners) listener(result, key);
  for (const listener of keysListeners) listener();
  return result;
}

/**
 * Cancel any pending debounced save for `key` without writing it.
 * Used by the post-send delete path so the optimistic clear that
 * scheduled an empty-body save can drop without re-touching IDB —
 * `markSent` already deletes the row directly.
 */
export function cancelPendingDraftSave(key: string): void {
  const entry = debounceQueue.get(key);
  if (!entry) return;
  clearTimeout(entry.timer);
  debounceQueue.delete(key);
}

/**
 * Subscribe to debounced-save completions. Receives every successful
 * (or no-op) write triggered through `saveDraftDebounced` /
 * `flushDraft`. Rare consumers — most callers prefer
 * `subscribeDraftKeys`.
 */
export function subscribeDraftSaves(listener: DebounceListener): () => void {
  debounceListeners.add(listener);
  return () => {
    debounceListeners.delete(listener);
  };
}

/* ─── key-set subscription ────────────────────────────────────── */

type KeysListener = () => void;
const keysListeners = new Set<KeysListener>();

/**
 * Subscribe to coarse "draft set changed" notifications. The callback
 * fires whenever a debounced save flushes, an explicit `saveDraft` /
 * `deleteDraft` runs through `notifyKeysChanged`, or a flush-on-submit
 * resolves. Consumers re-call `listDraftKeys` to pull the new set.
 *
 * The signal carries no payload by design: `getAllKeys` is cheap on
 * a small store, and a snapshot would race with concurrent writes
 * across tabs.
 */
export function subscribeDraftKeys(listener: KeysListener): () => void {
  keysListeners.add(listener);
  return () => {
    keysListeners.delete(listener);
  };
}

/**
 * Notify subscribers that the key set may have changed. Called
 * internally by the debounced flush; exported so post-send deletes
 * (which use `deleteDraft` directly) can also fan out.
 */
export function notifyDraftKeysChanged(): void {
  for (const listener of keysListeners) listener();
}
